import * as React from "react";
import { connect } from "react-redux";
import { List, Map } from "immutable";
import { withRouter } from "react-router";
import { pasteContainerStyles } from "./styles";
import { TargetFilterRow } from "./target-filter-row";
import { TargetValuesGrid, BLANK_ROW_IDENTIFIER, isValidTargetValue, isValidDoor } from "./target-values-grid";
import { IconAlertCircleRed } from "components/icons/assortments/icon-alert-circle-red";
import { formatMessage } from "lib/globalize-util";
import { IStore } from "reducers";
import { CollectionKeys, ValueTypes } from "constants/product-collection";
import { LookUpFieldMap } from "helpers/merchant/lookup/single-line-lookup-fields";
import { ISchemaKey, parseAssortmentId } from "helpers/product-collection";
import { TargetRowDataCache, useTargetWizardContext } from "../../../context/target-wizard-context";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionKind,
    BAG_VALUE_PARAMS_SEPARATOR,
    IAllocationDimension,
    getAllocationBagPath,
    getAllocationBagValue,
    setBagValue,
} from "helpers/allocation-dimension";
import ExcelManager from "helpers/excel-manager";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { TargetGroupWithTargets, ITargetsData } from "components/product-collection/footer/types";
import { DOORS_COLUMN } from "helpers/validators/import-targets-validator";
import { IValueTypeMap } from "helpers/merchant/lookup/types";
import { getAllocationDimensions } from "reducers/selectors/collection";
import { convertTargetsDataToExcel } from "./paste-container-utils";

const styles = pasteContainerStyles();

interface IProps {
    uploadedTargetData?: ITargetsData[];
}

type ReduxProps = {
    merchantId: string;
    itemDisplayPropertyKeys: List<Map<keyof ISchemaKey, any>>;
    targets?: List<any>;
    draft: DimensionsPanelReduxState;
    allocationDimensions: IAllocationDimension[];
};

export enum PasteError {
    Generic,
    ColumnCount,
    InvalidValues,
}

export const INVALID_VALUE = -1;

/**
 * Generates an error map for target ids based on what is in the current buffer and target row cache. For unknown values for target values will be flagged as an error
 * @param buffer
 * @param doorIdMap
 * @param targetRowDataCache
 */
export const generateTargetErrorMapForBuffer = (
    buffer: AllocationBag,
    doorIdMap: DoorIdMap,
    targetRowDataCache: TargetRowDataCache
): { [key: string]: PasteError | null | undefined } => {
    const map = {};

    buffer?.map((_, key) => {
        const splitValues = key.split(BAG_VALUE_PARAMS_SEPARATOR);
        let targetId;
        let type;
        if (splitValues.length === 2) {
            targetId = splitValues[0];
            type = splitValues[1];
        }

        let error: PasteError | null = null;

        // Add any errors that exist in the target data cache
        const cacheEntry: TargetRowData[] = targetRowDataCache[targetId];
        if (cacheEntry) {
            cacheEntry.forEach((row) => {
                if (
                    row.type !== BLANK_ROW_IDENTIFIER &&
                    !isValidDoor(row.externalDoorId, doorIdMap, cacheEntry, row.doorId)
                ) {
                    error = PasteError.InvalidValues;
                }
                if (row.deliveryIdMap) {
                    for (const key of Object.keys(row.deliveryIdMap)) {
                        const value = row.deliveryIdMap[key];
                        if (!isValidTargetValue(value)) {
                            error = PasteError.InvalidValues;
                            break;
                        }
                    }
                }
            });
        }

        if (type === `${AllocationBagType.target}`) {
            const targetBagValues = buffer.getIn(getAllocationBagPath(AllocationBagType.target, targetId));
            if (targetBagValues) {
                targetBagValues.map((_, key) => {
                    const splitValues = key.split(BAG_VALUE_PARAMS_SEPARATOR);
                    let deliveryId;
                    let currentDoorId;
                    if (splitValues.length === 2) {
                        deliveryId = splitValues[0];
                        currentDoorId = splitValues[1];
                    } else {
                        currentDoorId = splitValues[0];
                    }

                    const isNotRecognizedInternalDoorId = !doorIdMap.internal[currentDoorId];
                    if (isNotRecognizedInternalDoorId) {
                        error = PasteError.InvalidValues;
                    }

                    let value;
                    if (deliveryId && currentDoorId) {
                        value = getAllocationBagValue(AllocationBagType.target, buffer, targetId, [
                            deliveryId,
                            currentDoorId,
                        ]);
                    } else if (currentDoorId) {
                        value = getAllocationBagValue(AllocationBagType.target, buffer, targetId, [currentDoorId]);
                    }

                    if (value === INVALID_VALUE) {
                        error = PasteError.InvalidValues;
                    }
                });
            }
        }

        map[targetId] = error;
    });

    return map;
};

export interface DoorIdMap {
    external: {
        [externalId: string]: string;
    };
    internal: {
        [internalId: string]: string;
    };
}

export const parseDoorIdToMap = (allocationDimensions: IAllocationDimension[]) => {
    const genericAllocationDimensions = allocationDimensions.filter(
        (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Generic
    );

    const baseMap = {
        external: {},
        internal: {},
    };

    if (genericAllocationDimensions.length === 0) {
        return baseMap;
    }

    return genericAllocationDimensions[genericAllocationDimensions.length - 1].dimension_values.reduce(
        (acc, dimensionValue) => {
            acc.internal[dimensionValue.id] = dimensionValue.external_id;
            acc.external[dimensionValue.external_id] = dimensionValue.id;
            return acc;
        },
        baseMap
    );
};

export interface TargetRowData {
    doorId?: string;
    externalDoorId?: string;
    type?: string;
    deliveryIdMap?: { [key: string]: any };
    isNew?: boolean;
}

export const convertAllocationBagToTableDataRows = (
    targetId: string,
    allocationBag: AllocationBag,
    doorIdMap: DoorIdMap,
    doorValueIdxMap: { [key: string]: number }
): TargetRowData[] => {
    const rowData = [];

    const targetBagValues = allocationBag?.getIn(getAllocationBagPath(AllocationBagType.target, targetId));
    if (targetBagValues) {
        targetBagValues.map((_, key) => {
            const splitValues = key.split(BAG_VALUE_PARAMS_SEPARATOR);
            let deliveryId;
            let doorId;
            if (splitValues.length === 2) {
                deliveryId = splitValues[0];
                doorId = splitValues[1];
            } else if (splitValues.length === 1) {
                doorId = splitValues[0];
            }

            let doorRow = null;
            for (const row of rowData) {
                if (row.doorId === doorId) {
                    doorRow = row;
                    break;
                }
            }

            let value;
            if (doorId && deliveryId) {
                value = getAllocationBagValue(AllocationBagType.target, allocationBag, targetId, [deliveryId, doorId]);
            } else if (doorId) {
                value = getAllocationBagValue(AllocationBagType.target, allocationBag, targetId, [doorId]);
            }

            if (doorRow) {
                doorRow.deliveryIdMap[deliveryId] = value;
            } else {
                rowData.push({
                    isNew: false,
                    doorId,
                    externalDoorId: doorIdMap.internal[doorId] ? doorIdMap.internal[doorId] : doorId,
                    deliveryIdMap: {
                        [deliveryId]: value,
                    },
                });
            }
        });
    }

    rowData.sort((a, b) => {
        const aId = a.doorId;
        const bId = b.doorId;

        const aIdx = doorValueIdxMap[aId];
        const bIdx = doorValueIdxMap[bId];

        return aIdx - bIdx;
    });

    rowData.push({ type: BLANK_ROW_IDENTIFIER });
    return rowData;
};

export const updateTableDataRowsToAllocationBag = (
    targetId: string,
    allocationBag: AllocationBag,
    targetRowData: TargetRowData[],
    doorIdMap: DoorIdMap
): AllocationBag => {
    let modifiedMap: AllocationBag = Map(allocationBag);

    // Clear out everything for this target id
    const allocationBagPath = getAllocationBagPath(AllocationBagType.target, targetId);
    modifiedMap = modifiedMap.deleteIn(allocationBagPath);

    for (const row of targetRowData) {
        if (row.externalDoorId && row.type !== BLANK_ROW_IDENTIFIER) {
            const doorInternalId = doorIdMap.external[row.externalDoorId];
            if (row.deliveryIdMap && doorInternalId) {
                for (const key of Object.keys(row.deliveryIdMap)) {
                    const value = row.deliveryIdMap[key];
                    modifiedMap = setBagValue(
                        AllocationBagType.target,
                        modifiedMap,
                        targetId,
                        [key, doorInternalId],
                        isValidTargetValue(value) ? value : INVALID_VALUE
                    );
                }
            } else if (doorInternalId) {
                modifiedMap = setBagValue(AllocationBagType.target, modifiedMap, targetId, [doorInternalId], 0);
            } else {
                modifiedMap = setBagValue(
                    AllocationBagType.target,
                    modifiedMap,
                    targetId,
                    [row.externalDoorId],
                    INVALID_VALUE
                );
            }
        }
    }

    return modifiedMap;
};

export interface TargetGroupValues {
    lookUpFieldMap: LookUpFieldMap;
}

const generateValuesFromCollectionForTargetGroup = (
    itemDisplayPropertyKeys: List<Map<keyof ISchemaKey, any>>,
    targetGroupWithTargets: TargetGroupWithTargets
): TargetGroupValues => {
    const lookUpFieldMap: LookUpFieldMap = {};
    let latestTargetCreatedAt = 0;
    let latestTargetModifiedAt = 0;

    targetGroupWithTargets.targets.forEach((target) => {
        const dependenciesValues: IValueTypeMap = {};
        const schemaList: ISchemaKey[] = [];
        const createdAt = target.created_at_ms;
        const modifiedAt = target.modified_at_ms;
        if (createdAt > latestTargetCreatedAt) {
            latestTargetCreatedAt = createdAt;
        }

        if (modifiedAt > latestTargetModifiedAt) {
            latestTargetModifiedAt = modifiedAt;
        }

        const filters = target.filters;
        filters.forEach((filter) => {
            const fieldName = filter.field_name;
            const value = filter.value;

            itemDisplayPropertyKeys?.forEach((key) => {
                const keyFieldName = key.get("field_name");
                if (keyFieldName === fieldName) {
                    schemaList.push(key.toJS());
                    dependenciesValues[fieldName] = {
                        value,
                        type: key.get("value_type") as ValueTypes,
                    };
                }
            });
        });

        const targetId = target.id;
        lookUpFieldMap[targetId] = {
            schemaList,
            dependenciesValues,
        };
    });

    return {
        lookUpFieldMap,
    };
};

export const PasteContainerComponent: React.FunctionComponent<IProps & ReduxProps> = ({
    uploadedTargetData,
    merchantId,
    itemDisplayPropertyKeys,
    draft,
    allocationDimensions,
}) => {
    // Using ref since we don't need to listen to any new change
    const doorIdMapRef = React.useRef(parseDoorIdToMap(allocationDimensions));

    const [selectedTargetId, setSelectedTargetId] = React.useState<string | null>(null);
    const {
        targetGroupWithTargetsSnapshot: [targetGroupWithTargetsSnapshot],
        targetAllocationBagBuffer: [targetAllocationBagBuffer, setTargetAllocationBagBuffer],
        targetAllocationBagBufferIsValid: [, setTargetAllocationBagBufferIsValid],
        targetRowDataCache: [targetRowDataCache, setTargetRowDataCache],
    } = useTargetWizardContext();
    const [targetRowData, setTargetRowData] = React.useState<TargetRowData[]>([]);
    const [showError, setShowError] = React.useState<PasteError | null>(null);

    const selectedMetrics = React.useMemo(
        () =>
            draft?.metrics?.values.filter(
                (metric) => metric.isTargetMetric && metric.kind === targetGroupWithTargetsSnapshot.metric
            ),
        [draft, targetGroupWithTargetsSnapshot]
    );

    React.useEffect(() => {
        setSelectedTargetId(targetGroupWithTargetsSnapshot.targets[0].id);
    }, [targetGroupWithTargetsSnapshot]);

    React.useEffect(() => {
        if (uploadedTargetData.length) {
            let updatedAllocationBagBuffer: AllocationBag = Map(targetAllocationBagBuffer);
            targetGroupWithTargetsSnapshot.targets.forEach((target) => {
                let externalDoorId = "";
                let targetId = undefined;
                const rowData = [];
                const lastFilter = target.filters[target.filters.length - 1];
                uploadedTargetData.forEach((row) => {
                    // If lastFilter is undefined, then we are dealing with an assortment target
                    // If not, then we need use the rows that match the filters for the current target
                    if (
                        !lastFilter ||
                        (row[lastFilter.field_name] && row[lastFilter.field_name] === lastFilter.value)
                    ) {
                        targetId = target.id;
                        externalDoorId = row[DOORS_COLUMN];

                        const deliveries = {};
                        allocationDimensions[0]?.dimension_values.forEach((dimensionValue) => {
                            if (dimensionValue.included) {
                                const value = row[dimensionValue.external_id];
                                if (value) {
                                    deliveries[dimensionValue.id] = value;
                                }
                            }
                        });
                        const internalDoorId = doorIdMapRef.current.external[externalDoorId];
                        rowData.push({
                            isNew: false,
                            doorId: internalDoorId,
                            externalDoorId: externalDoorId,
                            deliveryIdMap: deliveries,
                        });
                    }
                });
                rowData.push({ type: BLANK_ROW_IDENTIFIER });
                if (targetId) {
                    targetRowDataCache[targetId] = rowData;
                    setTargetRowDataCache(targetRowDataCache);
                    // Update the bag buffer and store it temporarily in this variable
                    updatedAllocationBagBuffer = updateTableDataRowsToAllocationBag(
                        targetId,
                        updatedAllocationBagBuffer,
                        targetRowDataCache[targetId],
                        doorIdMapRef.current
                    );
                }
            });
            // Set the buffer to the new values from the file for the next render
            setTargetAllocationBagBuffer(updatedAllocationBagBuffer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadedTargetData]);

    React.useEffect(() => {
        if (targetRowDataCache[selectedTargetId]) {
            setTargetRowData(targetRowDataCache[selectedTargetId]);
        } else {
            setTargetRowData(
                convertAllocationBagToTableDataRows(
                    selectedTargetId,
                    targetAllocationBagBuffer,
                    doorIdMapRef.current,
                    doorValuesIdxMap
                )
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTargetId]);

    // Add an effect to save all table data to the allocation buffer
    React.useEffect(() => {
        if (targetRowData && targetRowData.length > 0) {
            setTargetAllocationBagBuffer(
                updateTableDataRowsToAllocationBag(
                    selectedTargetId,
                    targetAllocationBagBuffer,
                    targetRowData,
                    doorIdMapRef.current
                )
            );
            setTargetRowDataCache({ ...targetRowDataCache, [selectedTargetId]: targetRowData });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetRowData]);

    const { lookUpFieldMap } = React.useMemo(
        () => generateValuesFromCollectionForTargetGroup(itemDisplayPropertyKeys, targetGroupWithTargetsSnapshot),
        [itemDisplayPropertyKeys, targetGroupWithTargetsSnapshot]
    );

    const targetErrorMap = React.useMemo(
        () => generateTargetErrorMapForBuffer(targetAllocationBagBuffer, doorIdMapRef.current, targetRowDataCache),
        [targetAllocationBagBuffer, targetRowDataCache]
    );

    const doorValuesIdxMap = React.useMemo(() => {
        let doorValues = [];
        // When dimensions are length 2 then it's assumed the first dimension is deliveries and the second is doors
        if (draft.dimensions.length === 2) {
            doorValues = draft.dimensions[1].values;
        } else if (draft.dimensions.length == 1) {
            // When dimensions are length 1 assume the first dimension is doors
            doorValues = draft.dimensions[0].values;
        }

        return doorValues.reduce((acc, val, idx) => {
            acc[val.id] = idx;
            return acc;
        }, {});
    }, [draft.dimensions]);

    const errorMessage: string | null = React.useMemo(() => {
        const currentError = showError || targetErrorMap[selectedTargetId];

        switch (currentError) {
            case PasteError.Generic:
                return "Copy and paste values from a spreadsheet (excluding column headers) or enter quantities manually. Door # column must include only the door ID for valid locations. Delivery columns must must contain only numbers and/or decimals.";
            case PasteError.ColumnCount:
                return "The number of pasted delivery columns exceeds the number of delivery columns in the table. Go back to the previous screen to adjust the copied data, then try again.";
            case PasteError.InvalidValues:
                return "Some entered values are invalid. Review and fix the highlighted cells to continue.";
        }

        return null;
    }, [targetErrorMap, selectedTargetId, showError]);

    React.useEffect(() => {
        setShowError(targetErrorMap[selectedTargetId]);
    }, [targetErrorMap, selectedTargetId]);

    React.useEffect(() => {
        let foundError = false;
        for (const key of Object.keys(targetErrorMap)) {
            const value = targetErrorMap[key];
            if (value !== null && value !== undefined) {
                foundError = true;
                break;
            }
        }
        setTargetAllocationBagBufferIsValid(!foundError);
    }, [setTargetAllocationBagBufferIsValid, targetErrorMap]);

    const exportValuesToExcel = React.useCallback(() => {
        const dimensions = draft.dimensions;
        let excelData;

        // Generate target cache for excel export
        const targetRowDataCache: TargetRowDataCache = {};
        targetGroupWithTargetsSnapshot.targets.forEach((target) => {
            const targetId = target.id;
            targetRowDataCache[targetId] = convertAllocationBagToTableDataRows(
                targetId,
                targetAllocationBagBuffer,
                doorIdMapRef.current,
                doorValuesIdxMap
            );
        });

        if (dimensions.length === 2) {
            excelData = convertTargetsDataToExcel(
                targetGroupWithTargetsSnapshot,
                targetRowDataCache,
                dimensions[0].values
            );
        } else {
            excelData = convertTargetsDataToExcel(targetGroupWithTargetsSnapshot, targetRowDataCache, []);
        }
        ExcelManager.write(excelData, `${targetGroupWithTargetsSnapshot.name}.xlsx`);
    }, [draft.dimensions, targetGroupWithTargetsSnapshot, targetAllocationBagBuffer, doorValuesIdxMap]);

    return (
        <div {...styles} className="container">
            <span className="descriptionText">
                {formatMessage(
                    "Copy and paste values from a spreadsheet (excluding column headers) or enter quantities manually. Door # column must include only the door ID for valid locations. Delivery columns must must contain only numbers and/or decimals."
                )}
            </span>
            <div className="exportContainer">
                {errorMessage === null && (
                    <button className="exportButton" onClick={exportValuesToExcel}>
                        {formatMessage("Export values")}
                    </button>
                )}
            </div>
            <div className="tableTabContainer">
                <div className="targetListContainer">
                    {targetGroupWithTargetsSnapshot.targets.map((entry, idx) => (
                        <TargetFilterRow
                            key={entry.id}
                            merchantId={merchantId}
                            lookUpFieldMap={lookUpFieldMap}
                            targetId={entry.id}
                            isInError={!!targetErrorMap[entry.id]}
                            selected={entry.id === selectedTargetId}
                            isComplete={!targetErrorMap[entry.id]}
                            targetAllocationBagBuffer={targetAllocationBagBuffer}
                            setSelectedTargetId={setSelectedTargetId}
                            selectedMetrics={selectedMetrics ? selectedMetrics[0] : undefined}
                        />
                    ))}
                </div>
                <div className="targetValueGrid">
                    {errorMessage && (
                        <div className="targetValueGridErrorContainer">
                            <IconAlertCircleRed style={{ marginRight: "14px" }} />
                            {errorMessage}
                        </div>
                    )}
                    {selectedTargetId && (
                        <TargetValuesGrid
                            targetRowData={targetRowData}
                            setTargetRowData={setTargetRowData}
                            setError={(error) => {
                                setShowError(error);
                            }}
                            doorIdMap={doorIdMapRef.current}
                            selectedMetrics={selectedMetrics ? selectedMetrics[0] : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: IStore, ownProps) => {
    const { validAssortmentIds } = state.collection || {};

    const { isRollupMode, baseAssortmentId: _baseAssortmentId } = parseAssortmentId(ownProps?.params?.id);
    const baseAssortmentId =
        isRollupMode && validAssortmentIds?.length && validAssortmentIds[0] !== _baseAssortmentId
            ? validAssortmentIds[0]
            : _baseAssortmentId;

    const collection = state.mutate.snapshots && state.mutate.snapshots.get(baseAssortmentId);
    const merchantId = collection?.getIn(["merchant_v1_id", "string_value"], "");
    const itemDisplayPropertyKeys = collection?.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS);

    const allocationDimensions = getAllocationDimensions(state, baseAssortmentId);

    return {
        merchantId,
        itemDisplayPropertyKeys,
        draft: state.dimensionsPanel.draft,
        allocationDimensions,
    };
};

export const PasteContainer = withRouter(connect<ReduxProps, {}, IProps>(mapStateToProps, {})(PasteContainerComponent));
